import { formatDate } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  map,
  of,
  skip,
  switchMap,
  tap,
} from 'rxjs';

import { ExcelExportType, FilterService, FilterServiceActions } from '@ninety/platform/services';
import {
  ISSUES_TRASH_BACK_BUTTON_SOURCE,
  LayoutsIssuesEvents,
  selectShowIssuesTrash,
  ToolbarBackButtonEvents,
  NotificationActions,
  teamAll,
  TeamSelectors,
  UserListStateActions,
  FilterUserSelectors,
  selectLanguage,
  selectLanguageArea,
} from '@ninety/platform/state';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  WarningConfirmDialogComponent,
  PrintDialogComponent,
} from '@ninety/ui/core';
import { FileService } from '@ninety/ui/legacy/core/services/file.service';
import { PrintApi, PrintService } from '@ninety/ui/legacy/core/services/print.service';

import { IssuesV2Service } from '../../_shared/services/issues-v2.service';
import { IssuesActions } from '../issues.actions';
import { selectIssuesEntitiesDict, selectIssueV2IdFromRoute, selectSelectedIssueId } from '../issues.selectors';

import { IssuesTrashApiActions, IssuesTrashPageActions } from './trash.actions';
import {
  getDeleteForeverTrashConfirmDialogCopy,
  getDeleteForeverTrashSuccessMessage,
  getRestoreTrashConfirmDialogCopy,
  getRestoreTrashSuccessMessage,
  IssueTrashRow,
} from './trash.model';
import {
  selectIssuesTrashBackLabel,
  selectIssuesTrashExcelParams,
  selectIssuesTrashPrintParams,
  selectIssuesTrashQuery,
} from './trash.selectors';

@Injectable()
export class IssuesTrashEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly issuesService = inject(IssuesV2Service);
  private readonly filterService = inject(FilterService);
  private readonly dialog = inject(MatDialog);
  private readonly printService = inject(PrintService);
  private readonly fileService = inject(FileService);

  /**
   * The toolbar team selector isn't trash-aware, so re-query when the filter-bar team changes.
   * These effects stay subscribed for the app's lifetime once registered, so the `showTrash` gate
   * keeps them from firing while Trash is closed. `skip(1)` drops the first emission on
   * subscription (often `null` during bootstrap) before the truthy filter; a team changed
   * while Trash was closed is covered by the `Opened` reload.
   */
  teamChanged$ = createEffect(() =>
    this.store.select(TeamSelectors.selectFilterBarTeamId).pipe(
      skip(1),
      filter(Boolean),
      distinctUntilChanged(),
      concatLatestFrom(() => this.store.select(selectShowIssuesTrash)),
      filter(([, showTrash]) => showTrash),
      map(() => IssuesTrashPageActions.teamChanged())
    )
  );

  /**
   * The toolbar owner filter publishes through `UserListStateActions.changeFilterBarUsers`;
   * re-query trash when the selection changes while trash is open.
   */
  usersChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserListStateActions.changeFilterBarUsers),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesTrash),
        this.store.select(FilterUserSelectors.selectFilterBarUsers),
      ]),
      filter(([, showTrash, users]) => showTrash && !!users?.length),
      map(() => IssuesTrashPageActions.usersChanged())
    )
  );

  /**
   * The toolbar search box publishes through `FilterService.searchText$`; debounce typing and
   * re-query. Same lifetime caveat and `showTrash` gate as `teamChanged$` — every page shares
   * this search box.
   */
  searchChanged$ = createEffect(() =>
    this.filterService.searchText$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      skip(1),
      concatLatestFrom(() => [this.store.select(selectShowIssuesTrash), this.store.select(selectIssuesTrashQuery)]),
      // Skip emissions already reflected in state: entering trash clears the search box while the
      // reducer resets searchText, so without this the '' emission re-queries a fresh trash load.
      filter(([searchText, showTrash, query]) => showTrash && (searchText ?? '') !== query.searchText),
      map(([searchText]) => IssuesTrashPageActions.searchChanged({ searchText: searchText ?? '' }))
    )
  );

  /**
   * The toolbar search box is shared between the live list and trash; entering or leaving trash
   * starts a fresh search context, so clear the visible input on both transitions. (The reducer
   * already resets the slice's searchText via `issuesInitialState`.)
   */
  clearSearchOnTrashToggle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LayoutsIssuesEvents.showTrashItems, LayoutsIssuesEvents.hideTrashItems),
        tap(() => this.filterService.setSearchText(''))
      ),
    { dispatch: false }
  );

  /**
   * The toolbar Refresh button publishes through `FilterServiceActions.refreshed` for every page;
   * gate on `showTrash` (same lifetime caveat as `teamChanged$`) and re-run the trash query.
   */
  refreshClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.refreshed),
      concatLatestFrom(() => this.store.select(selectShowIssuesTrash)),
      filter(([, showTrash]) => showTrash),
      map(() => IssuesTrashPageActions.refreshed())
    )
  );

  printTrashPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.printPdf),
      filter(({ url }) => {
        const allowedPrintPaths = ['issues', 'short-term', 'long-term'];
        return allowedPrintPaths.some(allowed => url.toLowerCase().includes(allowed));
      }),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesTrash),
        this.store.select(selectIssuesTrashPrintParams),
      ]),
      filter(([, showTrash, printParams]) => showTrash && !!printParams),
      switchMap(([, , printParams]) =>
        this.dialog
          .open(PrintDialogComponent)
          .afterClosed()
          .pipe(
            filter(Boolean),
            switchMap(printOptions =>
              this.printService.openPdf(PrintApi.issues, { ...printParams!, printOptions }).pipe(
                // openPdf resolves a string (and shows its own error toast) on failure; only
                // an ArrayBuffer means the PDF actually rendered.
                filter((result): result is ArrayBuffer => result instanceof ArrayBuffer),
                map(() => NotificationActions.notify({ message: 'PDF printed successfully' })),
                catchError((error: unknown) =>
                  of(NotificationActions.notifyError({ message: 'Failed to print PDF', error }))
                )
              )
            )
          )
      )
    )
  );

  /**
   * Toolbar "Export to Excel" while Trash is open. The live `downloadExcel$` effect gates on
   * `!showTrash`, so this one owns the trash case: it hits `POST /trash/excel` (both ST+LT sheets
   * of deleted issues) and triggers a browser download, mirroring the live export's filename.
   */
  downloadTrashExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.exportToExcel),
      filter(({ exportType }) => exportType === ExcelExportType.issues),
      filter(({ url }) => {
        const allowedPaths = ['issues', 'short-term', 'long-term'];
        return allowedPaths.some(allowed => url.toLowerCase().includes(allowed));
      }),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesTrash),
        this.store.select(selectIssuesTrashExcelParams),
        this.store.select(TeamSelectors.selectFilterBarTeam),
        this.store.select(selectLanguageArea('issue')),
      ]),
      filter(([, showTrash, excelParams]) => showTrash && !!excelParams),
      switchMap(([, , excelParams, team, language]) =>
        this.issuesService.downloadTrashExcel(excelParams!).pipe(
          tap(response => {
            const teamName = this.fileService.sanitizeFilename(team.name);
            const sti = this.fileService.sanitizeFilename(language.shortTerm);
            const lti = this.fileService.sanitizeFilename(language.longTerm);
            const fileName = `Deleted_${sti}_${lti}_${teamName}_${formatDate(new Date(), 'MMddyyyy', 'en-US')}.xlsx`;
            this.fileService.downloadExcelFile(response, fileName);
          }),
          map(() => NotificationActions.notify({ message: 'Excel file downloaded successfully' })),
          catchError((error: unknown) =>
            of(NotificationActions.notifyError({ message: 'Failed to download Excel file', error }))
          )
        )
      )
    )
  );

  /** When trash opens, push the resolved back-button label into the shared toolbar state. */
  showBackButton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsIssuesEvents.showTrashItems),
      concatLatestFrom(() => this.store.select(selectIssuesTrashBackLabel)),
      map(([, label]) => ToolbarBackButtonEvents.show({ label, source: ISSUES_TRASH_BACK_BUTTON_SOURCE }))
    )
  );

  /**
   * Leaving the issues page closes trash — but only when trash is actually open, so a plain
   * page unmount doesn't reset the slice or clear another feature's toolbar back button.
   */
  closeTrashOnPageDestroy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.issuesListComponentDestroyed),
      concatLatestFrom(() => this.store.select(selectShowIssuesTrash)),
      filter(([, showTrash]) => showTrash),
      map(() => LayoutsIssuesEvents.hideTrashItems())
    )
  );

  /** When trash closes (via any path), clear the toolbar back button. */
  hideBackButton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutsIssuesEvents.hideTrashItems),
      map(() => ToolbarBackButtonEvents.hide())
    )
  );

  /** Toolbar back-button click → close trash (guard ensures only issues-trash responds). */
  onBackButtonClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ToolbarBackButtonEvents.clicked),
      filter(({ source }) => source === ISSUES_TRASH_BACK_BUTTON_SOURCE),
      map(() => LayoutsIssuesEvents.hideTrashItems())
    )
  );

  /**
   * Opening a live (non-trashed) issue while Trash is open — e.g. clicking an issue in the
   * notification panel — can't load in the detail card: the trash table replaces the issues list,
   * so the list-gated detail loader never runs and the card renders empty. Drop out of Trash so the
   * normal list mounts and loads the issue. Trash-row clicks are excluded — those issues are already
   * in the entities dict with `deleted: true` (clicking one should stay in Trash).
   */
  exitTrashToOpenLiveIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      concatLatestFrom(() => [
        this.store.select(selectShowIssuesTrash),
        this.store.select(selectIssueV2IdFromRoute),
        this.store.select(selectIssuesEntitiesDict),
      ]),
      filter(
        ([, showTrash, routeIssueId, entities]) => showTrash && !!routeIssueId && !entities[routeIssueId]?.deleted
      ),
      map(() => LayoutsIssuesEvents.hideTrashItems())
    )
  );

  /**
   * Leaving Trash via the toolbar "Back to <tab> Issues" link closes the aux detail outlet — but only
   * when it's empty or showing a trashed issue (invalid in the live list we're returning to). When we
   * leave Trash to open a *live* issue (see {@link exitTrashToOpenLiveIssue$}), the routed issue isn't
   * a trash row, so the detail stays open for the normal list to load it.
   *
   * Scoped to the back-button path on purpose. Leaving the issues section entirely is already handled
   * by the nav links themselves (`{ outlets: { detail: null, primary: ... } }`). Dispatching a
   * detail-close on page teardown instead re-navigates to the issues route (the aux outlet close has
   * no primary, so it resolves against the still-current `/issues/...` URL) and hijacks the outbound
   * navigation — that was the "click To-Dos from issues Trash → land back on Issues" bug.
   */
  closeDetailOnTrashExit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ToolbarBackButtonEvents.clicked),
      filter(({ source }) => source === ISSUES_TRASH_BACK_BUTTON_SOURCE),
      concatLatestFrom(() => [
        this.store.select(selectIssueV2IdFromRoute),
        this.store.select(selectIssuesEntitiesDict),
      ]),
      filter(([, routeIssueId, entities]) => !routeIssueId || !!entities[routeIssueId]?.deleted),
      map(() => IssuesActions.closeDetailView())
    )
  );

  /**
   * Row click opens the trashed issue in the detail card. The row is already a full issue in the
   * shared entities dict (trash load uses the same adapter), so reusing the live open path resolves
   * the route id from state via `setSelectedIssueOnReload$` — no `getIssue`/`GET /trash/:id` fetch.
   * The card renders read-only because the issue carries `deleted: true`.
   */
  openDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashPageActions.detailOpened),
      map(({ issue }) => IssuesActions.openInDetailViewRequested({ issue }))
    )
  );

  loadTrash$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LayoutsIssuesEvents.showTrashItems,
        IssuesTrashPageActions.paginationChanged,
        IssuesTrashPageActions.sortChanged,
        IssuesTrashPageActions.teamChanged,
        IssuesTrashPageActions.usersChanged,
        IssuesTrashPageActions.searchChanged,
        IssuesTrashPageActions.refreshed
      ),
      concatLatestFrom(() => [
        this.store.select(TeamSelectors.selectFilterBarTeamId),
        this.store.select(selectIssuesTrashQuery),
      ]),
      switchMap(([, teamId, { pageIndex, pageSize, intervalCode, sortField, sortDirection, searchText, userIds }]) => {
        if (!teamId) return of(IssuesTrashApiActions.loadTrashFailure());

        // 'all' is the transient All-teams selection being restored by resetAllTeamsSelection$.
        // Skip (not failure — that would flash the error banner): the restore changes the filter
        // bar team, which re-triggers this load via teamChanged$ while the Loading state persists.
        if (teamId === teamAll._id) return EMPTY;

        return this.issuesService
          .searchTrash({
            teamId,
            page: pageIndex,
            pageSize,
            intervalCode,
            sortField,
            sortDirection,
            searchText: searchText || undefined,
            ...(userIds ? { userIds } : {}),
          })
          .pipe(
            map(response =>
              IssuesTrashApiActions.loadTrashSuccess({
                rows: response.items as IssueTrashRow[],
                totalCount: response.totalCount,
              })
            ),
            catchError(() => of(IssuesTrashApiActions.loadTrashFailure()))
          );
      })
    )
  );

  confirmRestore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashPageActions.restoreRequested),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      exhaustMap(([{ issue }, language]) => {
        if (!issue._id) return EMPTY;

        const { title, message } = getRestoreTrashConfirmDialogCopy(issue, language);

        return this.dialog
          .open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            autoFocus: false,
            disableClose: true,
            width: '450px',
            maxWidth: 450,
            data: {
              title,
              message,
              confirmButtonText: 'Restore',
            },
          })
          .afterClosed()
          .pipe(
            filter(confirmed => !!confirmed),
            map(() => IssuesTrashPageActions.restoreConfirmed({ issue }))
          );
      })
    )
  );

  restore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashPageActions.restoreConfirmed),
      switchMap(({ issue }) => {
        // `_id` is guaranteed by confirmRestore$ (it won't confirm without one); bail quietly otherwise.
        const issueId = issue._id;
        if (!issueId) return EMPTY;

        return this.issuesService.restoreFromTrash(issueId).pipe(
          map(() => IssuesTrashApiActions.restoreSuccess({ id: issueId, archived: !!issue.archived })),
          catchError((error: unknown) => of(IssuesTrashApiActions.restoreFailure({ error })))
        );
      })
    )
  );

  restoreSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.restoreSuccess),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      map(([{ archived }, language]) =>
        NotificationActions.notify({
          message: getRestoreTrashSuccessMessage(archived, language),
          duration: 5000,
        })
      )
    )
  );

  closeDetailAfterRestore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.restoreSuccess),
      concatLatestFrom(() => this.store.select(selectSelectedIssueId)),
      filter(([{ id }, selectedId]) => selectedId === id),
      map(() => IssuesActions.closeDetailView())
    )
  );

  restoreFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.restoreFailure),
      map(({ error }) => NotificationActions.notifyError({ message: 'Unable to restore issue.', error }))
    )
  );

  confirmDeleteForever$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashPageActions.deleteForeverRequested),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      exhaustMap(([{ issue }, language]) => {
        if (!issue._id) return EMPTY;

        const { title, message } = getDeleteForeverTrashConfirmDialogCopy(issue, language);

        return this.dialog
          .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
            autoFocus: false,
            disableClose: true,
            width: '450px',
            maxWidth: 450,
            data: {
              title,
              message,
              confirmButtonText: 'Delete Forever',
            },
          })
          .afterClosed()
          .pipe(
            filter(confirmed => !!confirmed),
            map(() => IssuesTrashPageActions.deleteForeverConfirmed({ issue }))
          );
      })
    )
  );

  deleteForever$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashPageActions.deleteForeverConfirmed),
      switchMap(({ issue }) => {
        // `_id` is guaranteed by confirmDeleteForever$; bail quietly otherwise.
        const issueId = issue._id;
        if (!issueId) return EMPTY;

        return this.issuesService.deleteForeverFromTrash(issueId).pipe(
          map(() => IssuesTrashApiActions.deleteForeverSuccess({ id: issueId })),
          catchError((error: unknown) => of(IssuesTrashApiActions.deleteForeverFailure({ error })))
        );
      })
    )
  );

  deleteForeverSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.deleteForeverSuccess),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      map(([, language]) =>
        NotificationActions.notify({
          message: getDeleteForeverTrashSuccessMessage(language),
          duration: 5000,
        })
      )
    )
  );

  closeDetailAfterDeleteForever$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.deleteForeverSuccess),
      concatLatestFrom(() => this.store.select(selectSelectedIssueId)),
      filter(([{ id }, selectedId]) => selectedId === id),
      map(() => IssuesActions.closeDetailView())
    )
  );

  deleteForeverFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesTrashApiActions.deleteForeverFailure),
      map(({ error }) => NotificationActions.notifyError({ message: 'Unable to delete issue forever.', error }))
    )
  );
}
